import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { formatCurrency } from "../../core/currency.core";
import { useTranslation } from "../../core/translation.core";
import { useProductVariations } from "../../hooks/services/useFetchProducts";
import useRetailShop from "../../hooks/useRetailShop";
import { AttributeValueType } from "../../types/attribute";
import { Product, ProductAttribute } from "../../types/product";
import { PosModal } from "../reuseables/modal.comp";

// interface VariableProductProps {
//   parentProduct: Product;
//   setShowVariableProduct: Dispatch<SetStateAction<boolean>>;
// }

interface UniqueCodesValues {
  code: string;
  name: string;
  values: Set<AttributeValueType>;
}

export default function VariableProduct({
  parentProduct,
  setShowVariableProduct,
  addItem,
  updateItem,
  cart,
  getProductWarehouseId,
}) {
  const pos = useRetailShop();
  const { t, msg } = useTranslation();
  const queryClient = useQueryClient();
  const [quantity, setQuantity] = useState(1);
  const { data: variations } = useProductVariations(parentProduct.id);

  const [selectedVariant, setSelectedVariant] = useState<Product>();

  const allAttributes = variations?.flatMap((p) => p.attributes);
  //  @ts-ignore
  const uniqueCodeValues = getUniqueCodeValues(allAttributes);
  const [selectedValues, setSelectedValues] = useState({});

  useEffect(() => {
    const selectedVariant = variations?.find((variant) => {
      return hasMatchingAttributeValues(variant.attributes, selectedValues);
    });

    setSelectedVariant(selectedVariant);
  }, [selectedValues]);

  const handleClick = (a: AttributeValueType, code: string) => {
    setSelectedValues((prev) => {
      const selectedValues = { ...prev };
      if (prev[code] === a) {
        // delete if already exists
        delete selectedValues[code];
      } else {
        // add if missing
        selectedValues[code] = a;
      }
      return selectedValues;
    });
  };

  async function addCart(product: Product) {
    const inCart = cart?.items?.find((item: any) => item.product.id === product.id);
    if (!inCart) {
      const warehouseId = getProductWarehouseId(product);
      addItem({ product, quantity, warehouseId });
      queryClient.invalidateQueries({ queryKey: ["PosCartCount"] });
    } else {
      const updatedQuantity = inCart.quantity + 1;
      updateItem({ product, quantity: updatedQuantity, warehouseId: inCart.warehouseId });
    }

    setShowVariableProduct(false);
  }

  if (!uniqueCodeValues) return;

  return (
    //  @ts-ignore
    <PosModal setModalOpened={setShowVariableProduct} btnRef={null}>
      <section id="variable-product-modal" className="flex flex-col items-center w-4/5 mx-auto min-h-[400px]">
        <h2 className="self-start text-2xl md:text-3xl mb-6">
          {t(msg.product.detailsFor)} {parentProduct.name}
        </h2>
        {uniqueCodeValues.map((c, idx) => (
          <section key={idx} className="unique-code w-full mb-8">
            <h3 className="capitalize text-xl">
              {t(msg.pos.select)} {c.name}
            </h3>
            <div className="flex w-full gap-[4%] md:gap-[2%] flex-wrap">
              {Array.from(c.values).map((a, idx) => (
                <RowBtn
                  key={idx}
                  code={c.code}
                  codeValue={a}
                  handleClick={handleClick}
                  selectedCodeValue={selectedValues[c.code]}
                />
              ))}
            </div>
          </section>
        ))}

        <p className="self-stretch text-right text-xl md:text-2xl mb-3 md:mb-4" id="variable-prod-price">
          {t(msg.formLabels.price)}: {formatCurrency(selectedVariant?.price || 0, pos!.defaultCurrency)}
        </p>
        <button
          id="variable-prod-submit"
          data-submit-btn-active={!!selectedVariant}
          type="button"
          className="bg-blue-500 text-white disabled:bg-gray-400 px-8 py-3 broder-2 rounded-md w-full"
          disabled={!selectedVariant}
          onClick={() => addCart(selectedVariant!)}
        >
          {t(msg.form.submit)}
        </button>
      </section>
    </PosModal>
  );
}

interface RowBtnProps {
  code: string;
  codeValue: AttributeValueType;
  handleClick: (v: AttributeValueType, code: string) => void;
  selectedCodeValue: AttributeValueType;
}

const RowBtn = ({ code, codeValue, handleClick, selectedCodeValue }: RowBtnProps) => {
  if (!codeValue) {
    return null;
  }

  const isActive = codeValue === selectedCodeValue;

  return (
    <button
      data-row-btn-active={isActive}
      type="button"
      onClick={() => handleClick(codeValue, code)}
      className={`${
        isActive ? "bg-blue-500 text-white" : " bg-primary text-black dark:text-white"
      } basis-[48%] md:basis-[32%] shrink-0 grow-0 border-primary rounded-md border-2  px-3 py-2 cursor-pointer hover:bg-primary-dark hover:border-primary-dark mb-2`}
    >
      {codeValue}
    </button>
  );
};

const getUniqueCodeValues = (attributes: ProductAttribute[]) => {
  if (!attributes) {
    return;
  }

  return attributes.reduce((r: UniqueCodesValues[], i: ProductAttribute) => {
    const codeCaptured = r.find((x) => x.code === i.code)?.code;

    if (!codeCaptured) {
      // add object with code, values etc to  array
      return [...r, { code: i.code, name: i.name, values: new Set([i.value]) }];
    } else {
      // loop through records to find the already existing code and update the new value in the set
      return r.map((x) => {
        if (x.code !== codeCaptured) {
          return x;
        } else {
          return { ...x, values: x.values.add(i.value) };
        }
      });
    }
  }, []);
};

const hasMatchingAttributeValues = (attributes: ProductAttribute[], selectedValues: { [x: string]: AttributeValueType }) => {
  if (attributes.length !== Object.values(selectedValues).length) {
    return false;
  }

  return attributes.every((a) => {
    return selectedValues[a.code] === a.value;
  });
};
